import { useWorkspace } from "@cometchat-ai/auth/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TableBody,
  TableHeader,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { Cable, MoreHorizontal, Plus, Trash2 } from "../components/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useArchiveSkill, useSkills } from "../agents/api/skills";
import type { SkillResponse } from "../agents/api/types";
import {
  DataGrid,
  DataGridCell,
  DataGridHead,
  DataGridHeaderRow,
  DataGridRow,
} from "../components/data-grid";
import { EmptyState } from "../components/empty-state";
import { ListSkeleton } from "../components/list-skeleton";
import { PageShell } from "../components/page-shell";
import { formatApiError } from "../lib/format-error";

function DeleteEndpointDialog({
  slug,
  endpoint,
  open,
  onOpenChange,
}: {
  slug: string;
  endpoint: SkillResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("workspace");
  const archive = useArchiveSkill(slug);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={`mcp-delete-dialog-${endpoint.id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("mcp.delete.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("mcp.delete.body").replace("{{name}}", endpoint.name)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={archive.isPending} data-testid="mcp-delete-cancel">
            {t("mcp.delete.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={archive.isPending}
            data-testid="mcp-delete-confirm"
            onClick={(e) => {
              e.preventDefault();
              archive.mutate(endpoint.id, {
                onSuccess: () => {
                  toast.success(t("mcp.delete.toast"));
                  onOpenChange(false);
                },
                onError: (err) => {
                  toast.error(t("mcp.delete.failed"), {
                    description: formatApiError(err as Error),
                  });
                },
              });
            }}
          >
            {t("mcp.delete.confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function McpRoute() {
  const { t } = useTranslation("workspace");
  const { workspace } = useWorkspace();
  const skillsQuery = useSkills(workspace.slug);
  const [deleteTarget, setDeleteTarget] = useState<SkillResponse | null>(null);

  const endpoints = (skillsQuery.data?.items ?? []).filter(
    (s) => s.type === "mcp_server" && !s.isArchived,
  );

  return (
    <PageShell
      data-testid="mcp-page"
      className="p-0"
      actions={
        <Button
          asChild
          size="sm"
          className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
          data-testid="mcp-add-btn"
        >
          <Link to="new">
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            {t("mcp.addEndpoint")}
          </Link>
        </Button>
      }
    >
      {skillsQuery.isPending ? (
        <ListSkeleton
          rows={6}
          itemClassName="h-10 w-full"
          className="space-y-2 p-6"
          data-testid="mcp-skeleton"
        />
      ) : endpoints.length === 0 ? (
        <div className="px-6 py-12">
          <EmptyState
            variant="page"
            data-testid="mcp-empty"
            icon={Cable}
            title={t("mcp.empty.title")}
            description={t("mcp.empty.description")}
            action={
              <Button
                asChild
                variant="secondary"
                className="gap-1.5 rounded-xl hover:bg-secondary-hover"
                data-testid="mcp-empty-add-btn"
              >
                <Link to="new">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {t("mcp.addEndpoint")}
                </Link>
              </Button>
            }
          />
        </div>
      ) : (
        <DataGrid data-testid="mcp-table">
          <TableHeader>
            <DataGridHeaderRow>
              <DataGridHead className="w-[240px] pl-6 pr-3">{t("mcp.table.name")}</DataGridHead>
              <DataGridHead className="w-[280px]">{t("mcp.table.description")}</DataGridHead>
              <DataGridHead className="w-[260px]">{t("mcp.table.url")}</DataGridHead>
              <DataGridHead className="w-[130px]">{t("mcp.table.added")}</DataGridHead>
              <DataGridHead className="w-[88px] pr-6 text-right" divider={false}>
                {t("mcp.table.actions")}
              </DataGridHead>
            </DataGridHeaderRow>
          </TableHeader>
          <TableBody>
            {endpoints.map((ep) => {
              const cfg = ep.configJsonb as { url?: string; iconUrl?: string };
              return (
                <DataGridRow key={ep.id} data-testid={`mcp-row-${ep.id}`}>
                  <DataGridCell className="pl-6 pr-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar className="h-7 w-7 shrink-0 rounded-md">
                        {cfg.iconUrl ? (
                          <AvatarImage src={cfg.iconUrl} alt="" className="object-cover" />
                        ) : null}
                        <AvatarFallback className="rounded-md bg-muted text-[11px] font-medium uppercase text-foreground">
                          {ep.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate font-medium text-foreground">{ep.name}</span>
                    </div>
                  </DataGridCell>
                  <DataGridCell className="truncate">{ep.description || "—"}</DataGridCell>
                  <DataGridCell className="truncate font-mono text-xs">
                    {cfg.url ?? "—"}
                  </DataGridCell>
                  <DataGridCell className="whitespace-nowrap">
                    {new Date(ep.createdAt).toLocaleDateString()}
                  </DataGridCell>
                  <DataGridCell className="w-[88px] pr-6 text-right" divider={false}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label={t("mcp.table.actions")}
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
                          data-testid={`mcp-actions-${ep.id}`}
                        >
                          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem
                          className="gap-2 text-[13px] text-destructive focus:!bg-[color:color-mix(in_oklch,var(--color-danger)_12%,transparent)] focus:!text-destructive"
                          onClick={() => setDeleteTarget(ep)}
                          data-testid={`mcp-delete-btn-${ep.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                          {t("mcp.delete.confirm")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </DataGridCell>
                </DataGridRow>
              );
            })}
          </TableBody>
        </DataGrid>
      )}

      {deleteTarget ? (
        <DeleteEndpointDialog
          slug={workspace.slug}
          endpoint={deleteTarget}
          open={Boolean(deleteTarget)}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
        />
      ) : null}
    </PageShell>
  );
}
